/** Timeline playback speed helpers for the video editor (preview). */

import { sceneMultishotAudioSpanSeconds, sceneMultishotImages } from './songBasedVideoEditor';

export const MIN_CLIP_PLAYBACK_SEC = 0.25;
export const SCENE_BOUNDARY_EPSILON_SEC = 0.05;
export const MIN_PLAYBACK_SPEED = 0.25;
export const MAX_PLAYBACK_SPEED = 4;

export const PLAYBACK_SPEED_PRESETS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

export function clampPlaybackSpeed(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.min(MAX_PLAYBACK_SPEED, Math.max(MIN_PLAYBACK_SPEED, n));
}

/** Narration / scene clips on video or image lanes */
export function clipVideoPlaybackSpeed(clip) {
  return clampPlaybackSpeed(clip?.videoPlaybackSpeed);
}

/** Audio lane clips */
export function clipAudioPlaybackSpeed(clip) {
  return clampPlaybackSpeed(clip?.audioPlaybackSpeed);
}

/** Per-clip speed × master timeline speed (preview, layout, export). */
export function effectiveClipAudioPlaybackSpeed(clip, masterSpeed = 1) {
  return clampPlaybackSpeed(clipAudioPlaybackSpeed(clip) * clampPlaybackSpeed(masterSpeed));
}

/** Map 0.25×–4× to slider percent (25–400). */
export function playbackSpeedToSliderPercent(speed) {
  return Math.round(clampPlaybackSpeed(speed) * 100);
}

export function playbackSpeedFromSliderPercent(percent) {
  return clampPlaybackSpeed(Number(percent) / 100);
}

/** Hard ceiling for in-source trim/playback (probed file length, not narration audio slot). */
export function clipVideoMediaCapSeconds(clip) {
  if (!clip?.videoUrl) {
    return Math.max(MIN_CLIP_PLAYBACK_SEC, Number(clip?.duration) || 5);
  }
  const probed = Number(clip?.videoMediaDurationSeconds);
  if (probed > 0) return probed;
  const stored = Number(clip?.videoSourceDurationMax);
  const slotDur = Number(clip?.duration) || 0;
  if (stored > 0 && slotDur > 0 && stored >= slotDur - 0.05) {
    return Math.max(stored, slotDur);
  }
  if (stored > 0) return stored;
  const trimEnd = Number(clip?.trimEnd);
  if (trimEnd > 0 && slotDur > 0 && trimEnd < slotDur - 0.12) {
    return Math.max(trimEnd, MIN_CLIP_PLAYBACK_SEC);
  }
  if (slotDur > 0) return slotDur;
  return 5;
}

/** Source span (seconds) consumed along one playback pass at 1× — trim-aware for video. */
export function clipSourcePlaybackDuration(clip) {
  if (!clip?.videoUrl) {
    return Math.max(MIN_CLIP_PLAYBACK_SEC, Number(clip?.playbackDuration ?? clip?.duration ?? 5) || 5);
  }
  const mediaCap = clipVideoMediaCapSeconds(clip);
  const dur = clip.duration ?? 5;
  const teRequested = clip.trimEnd != null ? clip.trimEnd : dur;
  const te = Math.min(teRequested, mediaCap);
  const ts = Math.max(0, Math.min(clip.trimStart ?? 0, te - MIN_CLIP_PLAYBACK_SEC));
  return Math.max(MIN_CLIP_PLAYBACK_SEC, te - ts);
}

/** Timeline occupancy for a video/image clip (shrinks when sped up). */
export function clipVideoTimelineDuration(clip) {
  const multishotSlice = multishotClipSliceSeconds(clip);
  if (multishotSlice != null) {
    const s = clipVideoPlaybackSpeed(clip);
    return Math.max(MIN_CLIP_PLAYBACK_SEC / s, multishotSlice / s);
  }
  const base = clipSourcePlaybackDuration(clip);
  const s = clipVideoPlaybackSpeed(clip);
  return Math.max(MIN_CLIP_PLAYBACK_SEC / s, base / s);
}

/** Song multishot stanza slices: use explicit segment duration, not stale video playback span. */
export function multishotClipSliceSeconds(clip) {
  if (clip?.parentSceneId == null || clip?.multishotPart == null) return null;
  const slice = Number(clip.multishotSegmentDuration) || Number(clip.duration) || 0;
  return slice > 0 ? Math.max(MIN_CLIP_PLAYBACK_SEC, slice) : null;
}

/**
 * Full timeline block for a scene clip (visible media + last-frame hold when audio exceeds video).
 * Uses timelineSlotDuration / duration as the narration slot when longer than drawn video.
 */
export function clipVideoTimelineOccupancy(clip) {
  const visible = clip?.videoUrl
    ? clipVideoSnippetDrawDuration(clip)
    : clipVideoTimelineDuration(clip);
  const spd = clipVideoPlaybackSpeed(clip);
  const slotRaw = Number(clip?.timelineSlotDuration) || 0;
  const durRaw = Number(clip?.duration) || 0;
  const slotFromMeta = slotRaw > 0
    ? Math.max(MIN_CLIP_PLAYBACK_SEC, slotRaw / spd)
    : (durRaw > 0 ? Math.max(MIN_CLIP_PLAYBACK_SEC, durRaw / spd) : 0);

  if (clip?.videoUrl && clipHasUserVideoTrim(clip)) {
    const explicitExtendSlot = slotRaw > 0 && slotFromMeta > visible + EXTEND_GAP_EPSILON_SEC;
    if (!explicitExtendSlot) {
      return Math.max(visible, clipVideoTimelineDuration(clip));
    }
  }

  return Math.max(visible, slotFromMeta, clipVideoTimelineDuration(clip));
}

/** Lane blocking duration for drag/ripple — pitch uses visible trim only (no extend padding). */
export function clipVideoTimelineLaneDuration(clip) {
  if (clip?.pitchRecording || clip?.sourceType === 'pitch_segment') {
    return clipVideoSnippetDrawDuration(clip);
  }
  return clipVideoTimelineOccupancy(clip);
}

/**
 * Width for the video strip on the timeline only (visual).
 * When metadata duration is shorter than the segment slot (e.g. narration audio span vs mp4 length),
 * the bar stops at real media length; the remainder of the slot is blank track until the next clip.
 */
export function clipVideoSnippetDrawDuration(clip) {
  const spd = clipVideoPlaybackSpeed(clip);
  if (!clip?.videoUrl) return clipVideoTimelineDuration(clip);
  const mediaCap = clipVideoMediaCapSeconds(clip);
  const durMeta = clip.duration ?? 5;
  const teRequested = clip.trimEnd != null ? clip.trimEnd : durMeta;
  const te = Math.min(teRequested, mediaCap);
  const ts = Math.max(0, Math.min(clip.trimStart ?? 0, te - MIN_CLIP_PLAYBACK_SEC));
  const trimSpan = Math.max(MIN_CLIP_PLAYBACK_SEC, te - ts);
  return Math.max(MIN_CLIP_PLAYBACK_SEC / spd, trimSpan / spd);
}

/**
 * Visual width shared by the image lane and animation lane for one scene clip.
 * Uses the narration slot when set; otherwise matches visible video trim or image duration.
 */
export function imageLaneClipDrawDuration(clip, sourceVideo = null) {
  if (Number(clip?.imageLaneDuration) > 0) {
    return Number(clip.imageLaneDuration);
  }
  if (Number(clip?.timelineSlotDuration) > 0) {
    return Number(clip.timelineSlotDuration);
  }
  const videoClip = sourceVideo?.videoUrl ? sourceVideo : (clip?.videoUrl ? clip : null);
  if (videoClip?.videoUrl) {
    return clipVideoSnippetDrawDuration(videoClip);
  }
  return clipVideoTimelineDuration(clip);
}

/** Animation lane bar width — independent from image/video when set. */
export function animationLaneClipDrawDuration(clip, fallbackSec = 0) {
  if (Number(clip?.animationLaneDuration) > 0) {
    return Number(clip.animationLaneDuration);
  }
  return Math.max(MIN_CLIP_PLAYBACK_SEC, Number(fallbackSec) || MIN_CLIP_PLAYBACK_SEC);
}

/**
 * Maximum in-source seconds available for trim handles (full file length).
 * Stays at the original probed/uploaded length even after the visible window is trimmed.
 */
export function clipVideoMaxSourceSeconds(clip) {
  return clipVideoMediaCapSeconds(clip);
}

/** True when the clip uses a deliberate in-source trim shorter than full media. */
export function clipHasUserVideoTrim(clip, mediaCapSeconds = null) {
  if (!clip?.videoUrl) return false;
  const knownMedia = Math.max(
    Number(clip?.videoMediaDurationSeconds) || 0,
    Number(clip?.videoSourceDurationMax) || 0,
  );
  // Ignore placeholder trim before the file length is known (pre-probe bootstrap).
  if (!(knownMedia > MIN_CLIP_PLAYBACK_SEC + 0.12)) return false;
  const cap = Math.max(
    knownMedia,
    MIN_CLIP_PLAYBACK_SEC,
    Number(mediaCapSeconds) || clipVideoMediaCapSeconds(clip) || 0,
  );
  const ts = Math.max(0, Number(clip.trimStart) || 0);
  const te = clip.trimEnd != null ? Number(clip.trimEnd) : cap;
  if (ts > 0.05) return true;
  if (cap > MIN_CLIP_PLAYBACK_SEC + 0.12 && te < cap - 0.12) return true;
  const visible = clipVideoSnippetDrawDuration(clip);
  if (cap > MIN_CLIP_PLAYBACK_SEC + 0.12 && visible < cap - 0.12) return true;
  return false;
}

/** Clamp trim window so timeline snippet never exceeds probed media length. */
export function clampVideoClipTrimToMediaCap(clip) {
  if (!clip?.videoUrl) return clip;
  const cap = clipVideoMediaCapSeconds(clip);
  let ts = Math.max(0, Number(clip.trimStart) || 0);
  let te = clip.trimEnd != null ? Number(clip.trimEnd) : cap;
  te = Math.min(Math.max(ts + MIN_CLIP_PLAYBACK_SEC, te), cap);
  ts = Math.max(0, Math.min(ts, te - MIN_CLIP_PLAYBACK_SEC));
  const probed = Number(clip.videoMediaDurationSeconds) || 0;
  return {
    ...clip,
    trimStart: ts,
    trimEnd: te,
    playbackDuration: Math.max(MIN_CLIP_PLAYBACK_SEC, te - ts),
    videoSourceDurationMax: probed > 0 ? probed : cap,
  };
}

/** Map editor timeline time → in-source media time for preview / transition sync. */
export function clipPreviewMediaTime(clip, timelineTime) {
  if (!clip?.videoUrl) return 0;
  const spd = clipVideoPlaybackSpeed(clip);
  const ts = Math.max(0, Number(clip.trimStart) || 0);
  const te = clipVideoSourceTrimEndSeconds(clip) || Number(clip.trimEnd) || Number(clip.duration) || ts + 1;
  const local = Math.max(0, Number(timelineTime) - (Number(clip.startTime) || 0));
  const media = ts + local * spd;
  const hi = Math.max(ts + 0.001, te);
  return Math.min(hi, Math.max(ts, media));
}

/** Source timestamp (seconds) of the last visible frame — respects trim + probed media length. */
export function clipVideoSourceTrimEndSeconds(clip) {
  if (!clip?.videoUrl) return 0;
  const durMeta = clip.duration ?? 5;
  const teRequested = clip.trimEnd != null ? clip.trimEnd : durMeta;
  return Math.min(teRequested, clipVideoMediaCapSeconds(clip));
}

/**
 * When narration/audio exceeds visible video length, returns metadata for the red “extend” zone.
 * @returns {null | { videoTimelineSec, audioSec, gapSec, slotTimelineSec, trimStart, trimEndSource, playbackSpeed }}
 */
const EXTEND_GAP_EPSILON_SEC = 0.12;

/** Timeline slot length for export (prefers explicit slot over stale source `duration`). */
export function clipVideoExportTimelineSlotSeconds(videoClip) {
  const explicit = Number(videoClip?.timelineSlotDuration);
  if (explicit > 0) {
    return Math.max(MIN_CLIP_PLAYBACK_SEC, explicit);
  }
  return Math.max(MIN_CLIP_PLAYBACK_SEC, clipVideoTimelineDuration(videoClip));
}

/**
 * Timeline seconds this clip should occupy in server/local export (matches audio slot + extend gap).
 * @param {number} [masterSpeed=1] Master timeline audio speed (must match preview / audio export).
 */
export function clipVideoExportPlaybackSeconds(videoClip, audioClip = null, masterSpeed = 1) {
  const slotSec = clipVideoExportTimelineSlotSeconds(videoClip);
  const videoSec = clipVideoTimelineDuration(videoClip);
  if (!videoClip?.videoUrl) return Math.max(videoSec, slotSec);
  if (videoClip?.multishotPart != null) {
    return Math.max(videoSec, slotSec);
  }
  if (
    videoClip?.pitchRecording
    || videoClip?.sourceType === 'pitch_segment'
    || audioClip?.sourceType === 'pitch_segment'
  ) {
    return Math.max(videoSec, slotSec);
  }
  if (audioClip) {
    const extendGap = getVideoExtendGapForClip(videoClip, audioClip, masterSpeed);
    if (extendGap) return extendGap.slotTimelineSec;
  }
  return Math.max(videoSec, slotSec);
}

export function getVideoExtendGapForClip(videoClip, audioClip, masterSpeed = 1) {
  if (
    videoClip?.pitchRecording
    || videoClip?.sourceType === 'pitch_segment'
    || audioClip?.sourceType === 'pitch_segment'
  ) {
    return null;
  }
  // Multishot slices share one scene narration clip — never extend each slice to the full scene audio.
  if (videoClip?.multishotPart != null) return null;
  if (!videoClip?.videoUrl || !audioClip) return null;
  const videoTimelineSec = clipVideoSnippetDrawDuration(videoClip);
  const audioSec = clipAudioTimelineDuration(audioClip, 0, masterSpeed);
  const slotDur = Number(videoClip.timelineSlotDuration) || Number(videoClip.duration) || 0;
  const metaSlotTimelineSec = slotDur > 0
    ? Math.max(
      MIN_CLIP_PLAYBACK_SEC,
      Number(videoClip.timelineSlotDuration) > 0
        ? slotDur
        : slotDur / clipVideoPlaybackSpeed(videoClip),
    )
    : 0;
  if (
    metaSlotTimelineSec > 0
    && Math.abs(metaSlotTimelineSec - audioSec) <= EXTEND_GAP_EPSILON_SEC
    && videoTimelineSec >= audioSec - EXTEND_GAP_EPSILON_SEC
  ) {
    return null;
  }
  if (!(audioSec > videoTimelineSec + EXTEND_GAP_EPSILON_SEC)) return null;
  const gapSec = audioSec - videoTimelineSec;
  const slotTimelineSec = videoTimelineSec + gapSec;
  return {
    videoTimelineSec,
    audioSec,
    gapSec,
    slotTimelineSec,
    trimStart: Math.max(0, Number(videoClip.trimStart) || 0),
    trimEndSource: clipVideoSourceTrimEndSeconds(videoClip),
    playbackSpeed: clipVideoPlaybackSpeed(videoClip),
  };
}

/**
 * Align stored slot/duration with visible trim (or audio extend hold) after edge resize.
 */
export function syncVideoClipTimelineSlot(clip, pairedAudio = null, masterSpeed = 1) {
  if (!clip?.videoUrl && !clip?.imageUrl) return clip;
  if (clip.pitchRecording || clip.sourceType === 'pitch_segment') return clip;

  const spd = clipVideoPlaybackSpeed(clip);
  if (clip.imageUrl && !clip.videoUrl) {
    const visible = clipVideoTimelineDuration(clip);
    return {
      ...clip,
      timelineSlotDuration: visible,
      duration: visible,
      playbackDuration: visible,
    };
  }

  const visible = clipVideoSnippetDrawDuration(clip);
  const extendGap = getVideoExtendGapForClip(clip, pairedAudio, masterSpeed);
  if (extendGap) {
    const slot = extendGap.slotTimelineSec;
    return {
      ...clip,
      timelineSlotDuration: slot,
      duration: Math.max(MIN_CLIP_PLAYBACK_SEC, slot * spd),
    };
  }

  const slot = Math.max(MIN_CLIP_PLAYBACK_SEC, visible);
  return {
    ...clip,
    timelineSlotDuration: slot,
    duration: slot * spd,
    playbackDuration: Math.max(MIN_CLIP_PLAYBACK_SEC, clipSourcePlaybackDuration(clip)),
  };
}

export function isAudioTimelineClip(clip) {
  return Boolean(clip?.url) && !clip?.videoUrl && !clip?.imageUrl;
}

/** Timeline seconds for narration export — matches preview (clip.duration cap for scene-bound clips). */
export function clipNarrationAudioExportPlaybackSeconds(clip, fileDur = 0, masterSpeed = 1) {
  if (!isSceneBoundNarrationAudioClip(clip)) {
    return clipAudioTimelineDuration(clip, fileDur, masterSpeed);
  }
  const metaDur = Number(clip?.duration) || 0;
  const s = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
  if (metaDur > 0) {
    return Math.max(MIN_CLIP_PLAYBACK_SEC / s, metaDur / s);
  }
  const probed = Number(fileDur) || 0;
  const srcDur = Math.max(probed, Number(clip?.sourceDuration) || 0);
  return Math.max(MIN_CLIP_PLAYBACK_SEC / s, srcDur / s);
}

/** Timeline occupancy for audio clips (same stored duration semantics as editor resize). */
export function clipAudioTimelineDuration(clip, fileDur = 0, masterSpeed = 1) {
  const metaDur = Number(clip?.duration) || 0;
  if (metaDur > 0 && isSceneSfxAudioClip(clip)) {
    // Scene SFX files often outlast their scene; the clip length is the scene slot, not the file.
    const s = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
    return Math.max(MIN_CLIP_PLAYBACK_SEC / s, metaDur / s);
  }
  const sd = Number(clip?.sourceDuration) || metaDur;
  const src = Math.max(metaDur, Number(fileDur) || 0, sd, MIN_CLIP_PLAYBACK_SEC);
  const s = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
  return Math.max(MIN_CLIP_PLAYBACK_SEC / s, src / s);
}

function isSceneSfxAudioClip(clip) {
  return clip?.sourceType === 'scene_sfx'
    || clip?.sourceType === 'engine_sfx_stem'
    || String(clip?.id || '').startsWith('scene-sfx-')
    || String(clip?.id || '') === 'engine-sfx-stem';
}

export function isStoryBackgroundMusicClip(clip) {
  if (clip?.id === 'story-background-music') return true;
  if (clip?.source === 'background_music') return true;
  return false;
}

/** Extra gain in the editor preview for generated narration clips (TTS files are often quiet vs music). */
const NARRATION_CLIP_PREVIEW_GAIN = 1.35;

/**
 * HTMLAudioElement volume (0–1) for timeline preview. Applies a small boost to narration / segment clips only.
 */
export function effectiveNarrationAudioLaneVolume(clip) {
  const raw = clip?.volume;
  const base = raw != null && Number.isFinite(Number(raw))
    ? Math.min(1, Math.max(0, Number(raw)))
    : 0.8;
  const st = String(clip?.sourceType || '');
  const id = String(clip?.id || '');
  const isNarrationLike =
    st === 'scene_mix'
    || st === 'scene_segment'
    || id.startsWith('seg-audio-')
    || (id.startsWith('audio-') && Boolean(clip?.sceneId));
  if (!isNarrationLike) return base;
  return Math.min(1, base * NARRATION_CLIP_PREVIEW_GAIN);
}

/** Project/video slot length for the whole-story bed (clip.duration from narration). */
export function storyBackgroundMusicSlotDuration(clip) {
  const cap = Number(clip?.duration) || 0;
  return cap > 0 ? cap : 0;
}

/** Max timeline extent for story bed (sourceDuration / initial slot); duration is the trimmed window. */
export function storyBackgroundMusicMaxDuration(clip) {
  const sd = Number(clip?.sourceDuration) || 0;
  const meta = Number(clip?.duration) || 0;
  return Math.max(sd, meta, MIN_CLIP_PLAYBACK_SEC);
}

/**
 * Visible/playback span on the timeline (respects trim handles).
 * Shorter MP3s loop inside this window; cannot extend past maxDuration.
 */
export function clipStoryBackgroundMusicPlaybackDuration(clip, fileDur = 0, masterSpeed = 1) {
  if (!isStoryBackgroundMusicClip(clip)) {
    return clipAudioTimelineDuration(clip, fileDur, masterSpeed);
  }
  const metaDur = Number(clip?.duration) || 0;
  const s = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
  const trimmed = Math.max(MIN_CLIP_PLAYBACK_SEC / s, metaDur / s);
  const maxSec = storyBackgroundMusicMaxDuration(clip) / s;
  return Math.min(trimmed, maxSec);
}

/** Timeline bar width — same as trimmed playback span. */
export function clipStoryBackgroundMusicDrawDuration(clip, fileDur = 0, masterSpeed = 1) {
  return clipStoryBackgroundMusicPlaybackDuration(clip, fileDur, masterSpeed);
}

/** True when preview/export UI should mute this clip's embedded video soundtrack. */
export function effectiveVideoClipPreviewMuted(clip, masterMuted) {
  return Boolean(masterMuted) || Boolean(clip?.muted);
}

/** Embedded video lane audio is audible only when the video track is active and not master-muted. */
export function embeddedVideoLaneAudioEnabled(videoLaneActive, masterMuted) {
  return Boolean(videoLaneActive) && !Boolean(masterMuted);
}

/** Resolve preview mute/volume for embedded video audio on a timeline clip. */
export function resolveEmbeddedVideoPreviewAudio(clip, {
  videoLaneActive = true,
  videoMasterMuted = false,
  masterVolume = 0.8,
} = {}) {
  const masterMuted = Boolean(videoMasterMuted);
  const laneEnabled = embeddedVideoLaneAudioEnabled(videoLaneActive, masterMuted);
  const muted = !laneEnabled || effectiveVideoClipPreviewMuted(clip, masterMuted);
  return {
    muted,
    volume: muted ? 0 : effectiveVideoClipPreviewVolume(clip, false, masterVolume),
  };
}

/** Preview volume for embedded video audio (per-clip × master; 0 when muted). */
export function effectiveVideoClipPreviewVolume(clip, masterMuted, masterVolume) {
  if (effectiveVideoClipPreviewMuted(clip, masterMuted)) return 0;
  const clipVol = clip?.volume;
  const base = clipVol != null && Number.isFinite(Number(clipVol))
    ? Math.min(1, Math.max(0, Number(clipVol)))
    : 1;
  const master = Number(masterVolume);
  const m = Number.isFinite(master) ? Math.min(1, Math.max(0, master)) : 0.8;
  return Math.min(1, base * m);
}

/** Default embedded-video gain for new timeline clips (0–1). */
export const DEFAULT_VIDEO_EMBEDDED_AUDIO_VOLUME = 1;

/** Normalize video lane clip `muted` (only explicit true mutes embedded audio). */
export function normalizeVideoClipEmbeddedAudioMute(clip) {
  if (!clip || typeof clip !== 'object') return clip;
  return { ...clip, muted: clip.muted === true };
}

export function normalizeVideoTracksEmbeddedAudioMute(tracks) {
  return (tracks || [[]]).map((track) =>
    (track || []).map((c) => normalizeVideoClipEmbeddedAudioMute(c)),
  );
}

/**
 * Older editors set `muted: true` on video clips whenever a narration audio lane existed.
 * Unmute embedded video audio in that case so preview matches current behavior.
 */
export function stripLegacyVideoClipEmbeddedMute(clip, scene) {
  const c = normalizeVideoClipEmbeddedAudioMute(clip);
  if (!c?.videoUrl || !c.muted) return c;
  const hasSeparateNarration =
    Boolean(c.audioUrl)
    || Boolean(scene?.audio_url)
    || (Array.isArray(scene?.audio_timeline) && scene.audio_timeline.some((t) => t?.segment_audio_url));
  if (hasSeparateNarration) return { ...c, muted: false };
  return c;
}

/** Map timeline-local seconds → source file time for looping story bed. */
export function storyBackgroundMusicSourceTime(localTimelineSec, clip, fileDurSec = 0, masterSpeed = 1) {
  const spd = effectiveClipAudioPlaybackSpeed(clip, masterSpeed);
  const raw = Math.max(0, localTimelineSec) * spd;
  const fd = Number(fileDurSec) || 0;
  if (fd > 0.05) return raw % fd;
  return raw;
}

/**
 * Probe a remote video file length. MediaRecorder WebM often reports Infinity until seek-to-end.
 */
export function probeVideoFileDurationSeconds(url, timeoutMs = 10000) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(0);
      return;
    }
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        /* ignore */
      }
      const n = Number(value);
      resolve(Number.isFinite(n) && n > 0 ? n : 0);
    };
    const timer = setTimeout(() => finish(0), timeoutMs);
    video.onerror = () => finish(0);
    const readMetadataDuration = () => {
      const d = Number(video.duration);
      return Number.isFinite(d) && d > 0 && d !== Infinity ? d : 0;
    };
    video.onloadedmetadata = () => {
      const fromMeta = readMetadataDuration();
      if (fromMeta > 0) {
        finish(fromMeta);
        return;
      }
      const onSeeked = () => {
        const t = Number(video.currentTime);
        finish(t > 0 ? t : readMetadataDuration());
      };
      video.addEventListener('seeked', onSeeked, { once: true });
      try {
        video.currentTime = 1e10;
      } catch {
        finish(0);
      }
    };
    try {
      video.src = url;
    } catch {
      finish(0);
    }
  });
}

/** Resolve segment/scene id from a timeline clip (video, audio, or subtitle). */
export function timelineClipLinkedSceneId(clip) {
  if (!clip || typeof clip !== 'object') return '';
  if (clip.parentSceneId != null) return String(clip.parentSceneId);
  let raw = String(clip.sceneId || clip.id || '');
  raw = raw.replace(/^audio-/, '').replace(/^seg-audio-/, '').replace(/__image$/, '');
  if (raw.includes('__ms')) raw = raw.split('__ms')[0];
  if (raw.includes('-seg-')) raw = raw.split('-seg-')[0];
  return raw;
}

/** Per-scene start/end on the primary video lane (min start, max end across multishot slices). */
export function sceneTimeWindowsFromVideoClips(flatVideo) {
  const starts = {};
  const ends = {};
  [...(flatVideo || [])].forEach((clip) => {
    if (clip?.isBookend) return;
    const sid = timelineClipLinkedSceneId(clip);
    if (!sid) return;
    const st = Number(clip.startTime) || 0;
    const end = st + clipVideoTimelineOccupancy(clip);
    if (starts[sid] == null || st < starts[sid]) starts[sid] = st;
    if (ends[sid] == null || end > ends[sid]) ends[sid] = end;
  });
  return { starts, ends };
}

function sortedScenesByNumber(scenes) {
  return [...(scenes || [])]
    .filter((s) => s?.id)
    .sort(
      (a, b) =>
        (Number(a.scene_number) || Number(a.segment_number) || 0)
        - (Number(b.scene_number) || Number(b.segment_number) || 0),
    );
}

/** Canonical narration span for one scene (multishot audio window or longest segment/mix). */
export function sceneCanonicalNarrationSpanSeconds(scene) {
  if (sceneMultishotImages(scene).length > 0) {
    return sceneMultishotAudioSpanSeconds(scene);
  }
  let segEnd = 0;
  (Array.isArray(scene?.audio_timeline) ? scene.audio_timeline : []).forEach((t) => {
    if (!t?.segment_audio_url) return;
    segEnd = Math.max(segEnd, (Number(t.start_seconds) || 0) + (Number(t.duration_seconds) || 0));
  });
  const audioDur = Number(
    scene?.audio_duration_seconds ?? scene?.audio_duration ?? scene?.duration_seconds ?? scene?.duration,
  );
  const visual = Number(scene?.video_duration_seconds ?? scene?.duration_seconds ?? scene?.duration);
  return Math.max(MIN_CLIP_PLAYBACK_SEC, segEnd, audioDur > 0 ? audioDur : 0, visual > 0 ? visual : 0);
}

/** Alias used by timeline packers. */
export function sceneNarrationSpanSeconds(scene) {
  return sceneCanonicalNarrationSpanSeconds(scene);
}

/** Duration for one segment clip — extends the last segment to the full scene narration span. */
export function segmentAudioClipDurationSeconds(scene, timelineEntry, entryIndex, entryCount) {
  const startOffset = Number(timelineEntry?.start_seconds) || 0;
  const entryDur = Number(timelineEntry?.duration_seconds) || 0;
  const sceneSpan = sceneNarrationSpanSeconds(scene);
  if (entryIndex === entryCount - 1) {
    const needed = Math.max(MIN_CLIP_PLAYBACK_SEC, sceneSpan - startOffset);
    return Math.max(entryDur > 0 ? entryDur : MIN_CLIP_PLAYBACK_SEC, needed);
  }
  return entryDur > 0 ? entryDur : MIN_CLIP_PLAYBACK_SEC;
}

/**
 * Pack scenes back-to-back from t=0 using full narration span per scene.
 * Scene N+1 always starts where scene N audio ends.
 */
export function computeSequentialSceneStarts(scenes) {
  const starts = {};
  let cursor = 0;
  for (const scene of sortedScenesByNumber(scenes)) {
    const sid = String(scene.id);
    starts[sid] = cursor;
    cursor += sceneCanonicalNarrationSpanSeconds(scene);
  }
  return starts;
}

/** True when adjacent scene audio clips overlap in time (audio vs audio). */
export function narrationAudioLaneHasAdjacentOverlap(audioClips, scenes) {
  const sorted = sortedScenesByNumber(scenes);
  const byScene = {};
  (audioClips || []).forEach((ac) => {
    const sid = timelineClipLinkedSceneId(ac);
    if (!byScene[sid]) byScene[sid] = [];
    byScene[sid].push(ac);
  });
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const sid = String(sorted[i].id);
    const nextSid = String(sorted[i + 1].id);
    const sceneClips = byScene[sid] || [];
    const nextClips = byScene[nextSid] || [];
    if (!sceneClips.length || !nextClips.length) continue;
    const sceneEnd = Math.max(
      ...sceneClips.map((ac) => (Number(ac.startTime) || 0) + (Number(ac.duration) || 0)),
    );
    const nextStart = Math.min(...nextClips.map((ac) => Number(ac.startTime) || 0));
    if (sceneEnd > nextStart + SCENE_BOUNDARY_EPSILON_SEC) return true;
  }
  return false;
}

/** True when saved video scene blocks differ from canonical narration span. */
export function narrationVideoAudioSpanDrift(flatVideo, scenes) {
  const { starts, ends } = sceneTimeWindowsFromVideoClips(flatVideo);
  for (const scene of sortedScenesByNumber(scenes)) {
    const sid = String(scene.id);
    if (starts[sid] == null) continue;
    const videoSpan = (ends[sid] ?? starts[sid]) - starts[sid];
    const canonical = sceneCanonicalNarrationSpanSeconds(scene);
    if (Math.abs(videoSpan - canonical) > SCENE_BOUNDARY_EPSILON_SEC) return true;
  }
  return false;
}

/** True when any scene N audio clip extends past scene N+1 video start. */
export function narrationAudioLaneHasSceneOverlap(audioClips, flatVideo, scenes) {
  const { starts } = sceneTimeWindowsFromVideoClips(flatVideo);
  const sorted = sortedScenesByNumber(scenes);
  const nextStartBySceneId = {};
  sorted.forEach((scene, i) => {
    const next = sorted[i + 1];
    if (!next) return;
    const ns = starts[String(next.id)];
    if (ns != null) nextStartBySceneId[String(scene.id)] = ns;
  });

  return (audioClips || []).some((ac) => {
    const sid = timelineClipLinkedSceneId(ac);
    const nextStart = nextStartBySceneId[sid];
    if (nextStart == null) return false;
    const start = Number(ac.startTime) || 0;
    const end = start + (Number(ac.duration) || 0);
    return end > nextStart + SCENE_BOUNDARY_EPSILON_SEC;
  });
}

/** Trim narration clips only when they bleed into the next scene (not to short video windows). */
export function clampNarrationAudioLaneToSceneWindows(audioClips, flatVideo, scenes) {
  const { starts } = sceneTimeWindowsFromVideoClips(flatVideo);
  const sorted = sortedScenesByNumber(scenes);
  const nextStartBySceneId = {};
  sorted.forEach((scene, i) => {
    const next = sorted[i + 1];
    if (!next) return;
    const ns = starts[String(next.id)];
    if (ns != null) nextStartBySceneId[String(scene.id)] = ns;
  });

  return (audioClips || []).map((ac) => {
    const sid = timelineClipLinkedSceneId(ac);
    const start = Number(ac.startTime) || 0;
    const curDur = Number(ac.duration) || 0;
    const clipEnd = start + curDur;
    const nextStart = nextStartBySceneId[sid];
    if (nextStart == null || clipEnd <= nextStart + SCENE_BOUNDARY_EPSILON_SEC) {
      return ac;
    }
    const maxDur = nextStart - start;
    if (maxDur <= SCENE_BOUNDARY_EPSILON_SEC) return ac;
    const clampedDur = Math.max(MIN_CLIP_PLAYBACK_SEC, maxDur);
    const src = Number(ac.sourceDuration) || curDur;
    return {
      ...ac,
      duration: clampedDur,
      sourceDuration: Math.min(src, clampedDur),
    };
  });
}

/** Scene-bound narration clips (mix or per-segment) — preview must not exceed timeline duration. */
export function isSceneBoundNarrationAudioClip(clip) {
  const st = String(clip?.sourceType || '');
  const id = String(clip?.id || '');
  return st === 'scene_mix'
    || st === 'scene_segment'
    || id.startsWith('seg-audio-')
    || (id.startsWith('audio-') && Boolean(clip?.sceneId));
}
